/** A segunda etapa do login (SPEC-IDN-002 § 3).
 *
 * A senha já foi aceita e ainda não há sessão. O token de etapa vale cinco
 * minutos e vive **em memória**, no estado desta tela: guardá-lo em
 * `localStorage` o exporia a XSS pelo mesmo motivo que o access token nunca
 * mora lá.
 *
 * **Um campo só, e ele aceita as duas coisas.** Seis dígitos do autenticador ou
 * um código de recuperação — o servidor tenta o TOTP primeiro e cai para a
 * lista. Dois campos separados obrigariam a pessoa a classificar o que tem na
 * mão antes de digitar, e quem está com o telefone quebrado não quer responder
 * a mais uma pergunta.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";

interface Props {
  /** Chamado com o código. Quem monta a sessão é o provedor de sessão. */
  aoConfirmar: (codigo: string) => Promise<void>;
  /** Volta para o formulário de e-mail e senha. */
  aoCancelar: () => void;
}

/** Menor código aceito: seis dígitos do TOTP. O de recuperação tem onze. */
const MINIMO = 6;

export function DesafioDoSegundoFator({ aoConfirmar, aoCancelar }: Props) {
  const [codigo, setCodigo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const podeEnviar = codigo.trim().length >= MINIMO && !enviando;

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    setEnviando(true);
    setErro(null);
    try {
      await aoConfirmar(codigo.trim());
    } catch (falha) {
      setErro(
        falha instanceof ApiError
          ? falha.message
          : "Não foi possível confirmar. Tente de novo em instantes.",
      );
      // O campo é limpo: o código do autenticador muda a cada 30 s, e deixar o
      // valor recusado na tela convida a apertar "Confirmar" de novo com ele.
      setCodigo("");
      setEnviando(false);
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-6">
      <div className="flex w-full max-w-[420px] flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-display-md text-texto">Confirme que é você</h1>
          <p className="font-texto text-body text-texto-suave">
            Digite o código de seis dígitos do seu aplicativo autenticador. Se você perdeu o
            aparelho, use um dos códigos de recuperação que guardou.
          </p>
        </div>

        <form
          onSubmit={(evento) => void enviar(evento)}
          aria-label="Confirmar segundo fator"
          className="flex flex-col gap-4"
        >
          <Campo
            rotulo="Código"
            value={codigo}
            onChange={(evento) => {
              setCodigo(evento.target.value);
            }}
            // `one-time-code` é o que faz o iOS e o Android oferecerem o código
            // do teclado; sem ele a pessoa alterna de aplicativo para copiar.
            autoComplete="one-time-code"
            // `text` e não `numeric`: o campo também aceita o código de
            // recuperação, que tem letras. Teclado numérico ali travaria quem
            // mais precisa dele.
            inputMode="text"
            placeholder="000000"
            required
            autoFocus
          />

          <Botao type="submit" larguraTotal carregando={enviando} disabled={!podeEnviar}>
            Confirmar
          </Botao>

          {erro !== null && (
            <p role="alert" className="font-texto text-caption text-carmim">
              {erro}
            </p>
          )}

          <button
            type="button"
            onClick={aoCancelar}
            className="font-texto text-caption text-texto-suave underline underline-offset-2 hover:text-texto"
          >
            Entrar com outra conta
          </button>
        </form>
      </div>
    </main>
  );
}
